<template>
  <div>
    <div class="tile">
      <div v-if="isLoading" class="loading-spinner">
        <div class="spinner"></div>
        <p>Loading content...</p>
      </div>
      <div v-if="hasError" class="error-message">
        <p>Failed to load content. Please try again.</p>
      </div>

      <!-- Power BI Embedded Container -->
      <div
        v-if="isPowerBIReport && !isLoading && !hasError"
        ref="powerbiContainer"
        class="powerbiReport"
      ></div>

      <!-- Generic iframe for other content -->
      <iframe
        v-if="!isPowerBIReport && !isLoading && !hasError"
        class="powerbiReport"
        width="100%"
        :src="props.link"
        :title="props.name || 'Embedded Content'"
        frameborder="0"
        allowfullscreen="true"
        style="height: calc(100vh - 100px)"
        @load="handleLoad"
        @error="handleError"
      ></iframe>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, onBeforeUnmount } from "vue";
import * as pbi from "powerbi-client";

const props = defineProps({
  link: String,
  name: String,
});

const isLoading = ref(true);
const hasError = ref(false);
const powerbiContainer = ref<HTMLDivElement | null>(null);
let powerbiReport: pbi.Report | null = null;

const isPowerBIReport = computed(() => {
  return (
    props.link?.includes("powerbi.com") ||
    props.link?.includes("app.powerbi.com") ||
    props.link?.includes("powerbi")
  );
});

const embedPowerBIReport = async () => {
  if (!props.link) {
    hasError.value = true;
    isLoading.value = false;
    return;
  }

  try {
    isLoading.value = false; // Set to false first so the container renders
    hasError.value = false;

    // Wait for next tick to ensure the container is rendered
    await new Promise((resolve) => setTimeout(resolve, 0));

    if (!powerbiContainer.value) {
      console.error("Power BI container is not available after rendering");
      hasError.value = true;
      return;
    }

    // For Power BI reports with authentication handled by iframe,
    // we'll use the iframe approach with URL parameters instead of SDK
    // This avoids the token requirement while still hiding sensitivity labels

    let embedUrl = props.link;

    // Add URL parameters to hide sensitivity label and customize UI
    const params = new URLSearchParams();
    params.set("disableSensitivityBanner", "true");

    if (embedUrl.includes("?")) {
      embedUrl += "&" + params.toString();
    } else {
      embedUrl += "?" + params.toString();
    }

    // Create an iframe dynamically
    const iframe = document.createElement("iframe");
    iframe.src = embedUrl;
    iframe.width = "100%";
    iframe.height = "100%";
    iframe.style.border = "none";
    iframe.style.height = "calc(100vh - 100px)";
    iframe.setAttribute("allowfullscreen", "true");

    // Clear previous content
    powerbiContainer.value.innerHTML = "";
    powerbiContainer.value.appendChild(iframe);

    // Handle iframe load event
    iframe.onload = () => {
      console.log("Power BI Report loaded successfully");
      isLoading.value = false;
    };

    iframe.onerror = () => {
      console.error("Power BI Report failed to load");
      hasError.value = true;
      isLoading.value = false;
    };
  } catch (error) {
    console.error("Error embedding Power BI report:", error);
    hasError.value = true;
    isLoading.value = false;
  }
};

const handleLoad = () => {
  isLoading.value = false;
};

const handleError = () => {
  isLoading.value = false;
  hasError.value = true;
};

onMounted(() => {
  if (isPowerBIReport.value) {
    embedPowerBIReport();
  } else {
    // For non-Power BI content, set loading to false so iframe shows
    isLoading.value = false;
  }
});

watch(
  () => props.link,
  () => {
    if (isPowerBIReport.value) {
      embedPowerBIReport();
    } else {
      isLoading.value = false;
      hasError.value = false;
    }
  }
);

onBeforeUnmount(() => {
  if (powerbiContainer.value) {
    powerbiContainer.value.innerHTML = "";
  }
});
</script>
<style scoped>
.powerbiReport {
  border-radius: 3px;
  width: 100%;
  height: calc(100vh - 100px);
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.14);
}

.loading-spinner,
.error-message {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: calc(100vh - 100px);
  font-size: 18px;
  gap: 15px;
}

.loading-spinner {
  color: #666;
}

.error-message {
  color: #e64c3c;
}

.spinner {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3499cc;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.tile {
  background: white;
  border-radius: 3px;
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.14);
}
</style>
